// ui/src/components/kiosk/AlertNotifier.tsx
import { useEffect, useRef, useState } from 'react'
import { BellOff } from 'lucide-react'
import { newAlertIds, playAlertChime, useOpenAlerts, type KioskAlert } from '@/lib/kioskAlerts'
import { C } from '@/components/kiosk/kioskTheme'
import { Dot } from '@/components/kiosk/kioskUi'

interface Toast { id: string; severity: string; title: string; entity: string | null }

function sevColor(sev: string): string {
  return sev === 'critical' ? C.red : sev === 'error' ? C.amber : sev === 'warning' ? C.amber : C.cyan
}
function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000)
  if (s < 60) return `${Math.round(s)} s`
  if (s < 3600) return `${Math.round(s / 60)} min`
  if (s < 86400) return `${Math.round(s / 3600)} h`
  return `${Math.round(s / 86400)} j`
}

/** Full-screen list of currently-open alerts (read-only). Opened from the
 * status-strip alert counter — turns "just a number" into something you can
 * actually read. */
export function AlertsDetail() {
  const { data: open } = useOpenAlerts()
  const list = [...(open ?? [])].sort(
    (a, b) => (b.severity === 'critical' ? 1 : 0) - (a.severity === 'critical' ? 1 : 0)
      || Date.parse(a.opened_at) - Date.parse(b.opened_at),
  )
  if (list.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3" style={{ color: C.dim }}>
        <BellOff className="h-10 w-10" style={{ color: C.green }} />
        <p className="text-lg font-semibold" style={{ color: C.ink }}>Aucune alerte ouverte</p>
        <p className="text-sm">Tout est au vert.</p>
      </div>
    )
  }
  return (
    <div className="grid h-full grid-cols-2 content-start gap-2.5 overflow-hidden">
      {list.slice(0, 12).map((a: KioskAlert) => {
        const col = sevColor(a.severity)
        return (
          <div key={a.id} className="kcard flex items-center gap-3 rounded-xl border px-3.5 py-2.5"
            style={{ background: C.panel, borderColor: `${col}44` }}>
            <Dot color={col} pulse={a.severity === 'critical'} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold" style={{ color: C.ink }}>{a.title}</div>
              <div className="truncate text-[11px]" style={{ color: C.dim }}>
                {a.entity_name ?? '—'} · <span className="kmono">depuis {ago(a.opened_at)}</span>
              </div>
            </div>
            <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
              style={{ color: col, background: `${col}1a`, border: `1px solid ${col}33` }}>{a.severity}</span>
          </div>
        )
      })}
    </div>
  )
}

export function AlertNotifier() {
  const { data: open } = useOpenAlerts()
  const seen = useRef<Set<string> | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = (id: string) => {
    const t = timers.current.get(id)
    if (t) {
      clearTimeout(t)
      timers.current.delete(id)
    }
    setToasts((cur) => cur.filter((x) => x.id !== id))
  }

  useEffect(() => {
    if (!open) return
    // Boot seed: first time we see the open set, mark all as seen — no storm.
    if (seen.current === null) {
      seen.current = new Set(open.map((a) => a.id))
      return
    }
    const fresh = newAlertIds(seen.current, open)
    if (fresh.length === 0) return
    const byId = new Map(open.map((a: KioskAlert) => [a.id, a]))
    const worst = fresh.map((id) => byId.get(id)!).sort(
      (a, b) => (a.severity === 'critical' ? -1 : 1) - (b.severity === 'critical' ? -1 : 1),
    )[0]
    playAlertChime(worst.severity)
    fresh.forEach((id) => seen.current!.add(id))
    setToasts((cur) => [
      ...fresh.map((id) => {
        const a = byId.get(id)!
        return { id: a.id, severity: a.severity, title: a.title, entity: a.entity_name }
      }),
      ...cur,
    ])
    fresh.forEach((id) => {
      const t = setTimeout(() => dismiss(id), 20_000)
      timers.current.set(id, t)
    })
  }, [open])

  // Clear any pending timers on unmount.
  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach((t) => clearTimeout(t))
      pending.clear()
    }
  }, [])

  if (toasts.length === 0) return null
  return (
    <div className="absolute inset-x-0 top-0 z-20 flex flex-col gap-1 p-2">
      {toasts.map((t) => {
        const crit = t.severity === 'critical'
        return (
          // Solid accent fill with near-black text. The previous Tailwind
          // classes (bg-amber-950/80) were dark-mode only: composited over a
          // white page they came out BROWN, not amber.
          <button
            key={t.id}
            onClick={() => dismiss(t.id)}
            className={`flex items-center gap-3 rounded-lg px-4 py-2 text-left ${crit ? 'animate-pulse' : ''}`}
            style={{
              background: crit ? C.red : C.amber,
              color: C.onAccent,
              border: `2px solid ${C.onAccent}22`,
            }}
          >
            <span
              className="inline-block h-3 w-3 shrink-0 rounded-full"
              style={{ background: C.onAccent, opacity: 0.85 }}
            />
            <span className="font-semibold">{t.title}</span>
            {t.entity && <span className="text-sm opacity-75">· {t.entity}</span>}
            <span className="ml-auto shrink-0 text-xs opacity-70">touche pour fermer</span>
          </button>
        )
      })}
    </div>
  )
}
